import React, { useEffect, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, ExpandMore, Search } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { PlaceOrderService } from '../../services/placeOrder/PlaceOrderService';
import { PendingPlaceOrderDto } from '../../interfaces/PendingPlaceOrderDto';

const PendingPlaceOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<PendingPlaceOrderDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');

    PlaceOrderService.getPendingList()
      .then((response) => {
        setOrders(response.data || []);
      })
      .catch((err: any) => {
        setOrders([]);
        setError(err?.data?.detail || 'Não foi possível carregar as solicitações pendentes.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, itemsPerPage]);

  const normalize = (value: string) =>
    (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const filteredOrders = orders.filter((order) => {
    if (!search) return true;
    const query = normalize(search);
    const placeName = normalize(order.placeName || '');
    const description = normalize(order.orderDescription || '');
    return (
      placeName.includes(query) ||
      description.includes(query) ||
      String(order.placeOrderCode).includes(query)
    );
  });

  const paginatedOrders = filteredOrders.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const pageCount = Math.ceil(filteredOrders.length / itemsPerPage);

  const formatDate = (value?: string | null) => {
    if (!value) return '';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '';
    const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
    const parsed = new Date(hasTimezone ? value : `${value}Z`);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsed);
  };

  return (
    <Box
      sx={{
        maxWidth: 960,
        mx: 'auto',
        my: { xs: 8, md: 4 },
        px: { xs: 2, md: 3 },
        py: { xs: 1, md: 2 },
        borderRadius: 3,
        background: 'linear-gradient(180deg, #f6fbff 0%, #ffffff 65%)',
      }}
    >
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: '1px solid #e3edf7',
            background: 'linear-gradient(135deg, #ffffff 0%, #f1f7ff 100%)',
          }}
        >
          <Box mb={2}>
            <Button
              variant="text"
              size="small"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Voltar
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight="bold" color="primary.main" gutterBottom>
                Solicitações pendentes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avalie as solicitações abertas para os seus espaços.
              </Typography>
            </Box>
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 999,
                bgcolor: '#fff3d6',
                color: '#8a5b00',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
              }}
            >
              Pendentes
            </Box>
          </Box>
        </Paper>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por espaço, descrição ou código"
            size="small"
            sx={{ minWidth: { xs: '100%', sm: 500 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl variant="standard" sx={{ minWidth: 100 }}>
            <InputLabel id="items-per-page-label">Itens</InputLabel>
            <Select
              labelId="items-per-page-label"
              value={itemsPerPage}
              onChange={(e) => {
                setPage(1);
                setItemsPerPage(Number(e.target.value));
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {!!error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && filteredOrders.length === 0 && (
          <Alert severity="info">Nenhuma solicitação pendente encontrada.</Alert>
        )}

        {!loading && !error && filteredOrders.length > 0 && (
          <Stack spacing={2}>
            {paginatedOrders.map((order) => (
              <Paper
                key={order.placeOrderId}
                elevation={0}
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 2,
                  border: '1px solid #e6eef7',
                  background: 'linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)',
                  boxShadow: '0 10px 24px rgba(15, 76, 129, 0.06)',
                }}
              >
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between',
                      gap: 1,
                      alignItems: { xs: 'flex-start', sm: 'center' },
                    }}
                  >
                    <Box>
                      <Typography fontWeight={700}>{order.placeName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Solicitado em {formatDateTime(order.requestDate) || '---'}
                      </Typography>
                    </Box>
                    <Typography
                      component="a"
                      href={`/solicitacao?uuid=${encodeURIComponent(order.placeOrderCode)}`}
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                    >
                      Ver solicitação
                    </Typography>
                  </Box>

                  <Divider sx={{ borderColor: '#e6eef7' }} />

                  <Accordion
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid #e6eef7',
                      backgroundColor: '#f9fbff',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Detalhes do evento
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary">
                        Data do evento: {formatDate(order.orderEventDate) || 'Não informada'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {order.orderDescription || 'Sem descrição.'}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {pageCount > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default PendingPlaceOrdersPage;
